import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Bubble, BubbleContent } from '@/components/ui/bubble';
import { Message, MessageContent } from '@/components/ui/message';
import type { InteractiveSpec } from '@/types';

/**
 * Lazy, like components/message-bubble.tsx does it.
 *
 * This route is a local harness that no student ever reaches, but a static
 * import here put json-render, the interactive registry, MathLive, Recharts and
 * the markdown pipeline into the route tree — and therefore into every bundle,
 * including the Connect-only element that has no interactive messages at all.
 */
const InteractiveMessage = lazy(() =>
	import('@/components/interactive-message').then((m) => ({ default: m.InteractiveMessage })),
);

export const Route = createFileRoute('/interactive-demo')({
	component: InteractiveDemoPage,
});

/**
 * Local mirrors of the production interactive specs for exercising the
 * json-render mechanism (catalog + registry + action round-trip) without a
 * backend. The authoritative specs live in
 * cdk/lambda/agent-handler/interactive-specs.ts — keep these in sync when
 * that file changes. `chart` and `step-explainer` are data-driven
 * (buildDataDrivenSpec composes them from an LLM-supplied JSON payload);
 * the instances below are representative outputs of that builder.
 */

// --- Homework Help: Subject Picker ---
const homeworkHelp: InteractiveSpec = {
	root: 'hw-card',
	elements: {
		'hw-card': {
			type: 'Card',
			props: {
				title: '📚 What subject do you need help with?',
				description: 'Pick one and we’ll get started!',
			},
			children: ['hw-subjects'],
		},
		'hw-subjects': {
			type: 'Columns',
			props: { columns: 2 },
			children: ['btn-math', 'btn-science', 'btn-english', 'btn-social', 'btn-health', 'btn-other'],
		},
		'btn-math': {
			type: 'Button',
			props: { label: '📐 Math', variant: 'outline', size: 'sm' },
			on: {
				press: { action: 'auto_send', params: { text: '[SUBJECT:math] Math' } },
			},
		},
		'btn-science': {
			type: 'Button',
			props: { label: '🔬 Science', variant: 'outline', size: 'sm' },
			on: {
				press: {
					action: 'auto_send',
					params: { text: '[SUBJECT:science] Science' },
				},
			},
		},
		'btn-english': {
			type: 'Button',
			props: { label: '📝 English', variant: 'outline', size: 'sm' },
			on: {
				press: {
					action: 'auto_send',
					params: { text: '[SUBJECT:english] English' },
				},
			},
		},
		'btn-social': {
			type: 'Button',
			props: {
				label: '🌍 Social Studies',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: '[SUBJECT:social-studies] Social Studies' },
				},
			},
		},
		'btn-health': {
			type: 'Button',
			props: { label: '💪 Health', variant: 'outline', size: 'sm' },
			on: {
				press: {
					action: 'auto_send',
					params: { text: '[SUBJECT:health] Health' },
				},
			},
		},
		'btn-other': {
			type: 'Button',
			props: {
				label: '📚 Something else',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: '[SUBJECT:other] Something else' },
				},
			},
		},
	},
};

// --- Homework Mode: Help Style Selector ---
const homeworkMode: InteractiveSpec = {
	root: 'hwmode-card',
	elements: {
		'hwmode-card': {
			type: 'Card',
			props: {
				title: 'How do you want me to help?',
				description: 'Pick your style — you can always change later.',
			},
			children: ['hwmode-buttons'],
		},
		'hwmode-buttons': {
			type: 'ButtonGroup',
			props: { direction: 'vertical' },
			children: ['btn-stepbystep', 'btn-guidance', 'btn-feedback'],
		},
		'btn-stepbystep': {
			type: 'Button',
			props: {
				label: '✏️ Walk me through it step by step',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Please walk me through the solution step by step.' },
				},
			},
		},
		'btn-guidance': {
			type: 'Button',
			props: {
				label: '💡 Give me hints so I can figure it out',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Give me hints and guide me to the answer.' },
				},
			},
		},
		'btn-feedback': {
			type: 'Button',
			props: {
				label: '📋 Check my work',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'I want you to check my work and give me feedback.' },
				},
			},
		},
	},
};

// --- Homework Quiz Prompt ---
const homeworkQuiz: InteractiveSpec = {
	root: 'quiz-card',
	elements: {
		'quiz-card': {
			type: 'Card',
			props: { title: 'Quick Quiz Time!', description: null },
			children: ['quiz-emoji', 'quiz-text', 'quiz-buttons'],
		},
		'quiz-emoji': {
			type: 'Emoji',
			props: { emoji: '🧠', size: 'lg' },
		},
		'quiz-text': {
			type: 'Text',
			props: {
				content: "I'll ask you a question about what we just covered. Ready?",
			},
		},
		'quiz-buttons': {
			type: 'ButtonGroup',
			props: { direction: 'horizontal' },
			children: ['btn-go', 'btn-easy', 'btn-hard'],
		},
		'btn-go': {
			type: 'Button',
			props: {
				label: "🚀 Let's go!",
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: { action: 'auto_send', params: { text: 'Yes, quiz me!' } },
			},
		},
		'btn-easy': {
			type: 'Button',
			props: {
				label: '😊 Make it easy',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Quiz me but start with an easy question.' },
				},
			},
		},
		'btn-hard': {
			type: 'Button',
			props: {
				label: '🔥 Make it hard',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Give me a challenging question!' },
				},
			},
		},
	},
};

// --- Study Tools Picker ---
const studyTools: InteractiveSpec = {
	root: 'study-chips',
	elements: {
		'study-chips': {
			type: 'ChipGroup',
			props: { direction: 'horizontal' },
			children: ['chip-flashcard', 'chip-simple', 'chip-realworld', 'chip-practice'],
		},
		'chip-flashcard': {
			type: 'Chip',
			props: { label: 'Flashcards', emoji: '🗂️' },
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: '[STUDY_METHOD:flashcards] Create flashcards for this topic.',
					},
				},
			},
		},
		'chip-simple': {
			type: 'Chip',
			props: { label: 'Simplify', emoji: '🎯' },
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: '[STUDY_METHOD:simplify] Explain this concept in really simple terms.',
					},
				},
			},
		},
		'chip-realworld': {
			type: 'Chip',
			props: { label: 'Real-world', emoji: '🌎' },
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: '[STUDY_METHOD:example] Give me a real-world example of this concept.',
					},
				},
			},
		},
		'chip-practice': {
			type: 'Chip',
			props: { label: 'Practice', emoji: '✏️' },
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: '[STUDY_METHOD:practice] Give me some practice problems to try.',
					},
				},
			},
		},
	},
};

// --- Homework Progress / Encouragement ---
const homeworkProgress: InteractiveSpec = {
	root: 'progress-card',
	elements: {
		'progress-card': {
			type: 'Card',
			props: { title: 'Nice work!', description: null },
			children: ['progress-emoji', 'progress-text', 'sep', 'progress-buttons'],
		},
		'progress-emoji': {
			type: 'Emoji',
			props: { emoji: '⭐', size: 'lg' },
		},
		'progress-text': {
			type: 'Text',
			props: {
				content: 'You got it! Every problem you solve makes you stronger. 💪',
			},
		},
		sep: { type: 'Separator', props: {} },
		'progress-buttons': {
			type: 'ButtonGroup',
			props: { direction: 'horizontal' },
			children: ['btn-another', 'btn-harder', 'btn-different'],
		},
		'btn-another': {
			type: 'Button',
			props: {
				label: '🔄 Another problem',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Give me another problem like that one.' },
				},
			},
		},
		'btn-harder': {
			type: 'Button',
			props: {
				label: '📈 Try harder one',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'I want to try a harder problem now.' },
				},
			},
		},
		'btn-different': {
			type: 'Button',
			props: {
				label: '🔀 Different topic',
				variant: 'secondary',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: "Let's work on something else." },
				},
			},
		},
	},
};

// --- Explain More / Alternative Approaches ---
const explainMore: InteractiveSpec = {
	root: 'explain-card',
	elements: {
		'explain-card': {
			type: 'Card',
			props: { title: 'Let me try a different approach', description: null },
			children: ['explain-buttons'],
		},
		'explain-buttons': {
			type: 'ButtonGroup',
			props: { direction: 'vertical' },
			children: ['btn-smaller', 'btn-analogy', 'btn-visual', 'btn-basics'],
		},
		'btn-smaller': {
			type: 'Button',
			props: {
				label: '🔍 Break it into smaller steps',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Can you break that down into smaller steps?' },
				},
			},
		},
		'btn-analogy': {
			type: 'Button',
			props: {
				label: '💭 Use an analogy',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: 'Can you explain it with an analogy or real-life example?',
					},
				},
			},
		},
		'btn-visual': {
			type: 'Button',
			props: {
				label: '🖼️ Describe it visually',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: { text: 'Can you describe this visually or with a diagram?' },
				},
			},
		},
		'btn-basics': {
			type: 'Button',
			props: {
				label: '📖 Start from the basics',
				variant: 'outline',
				size: 'sm',
			},
			on: {
				press: {
					action: 'auto_send',
					params: {
						text: 'I think I need to go back to the basics on this one.',
					},
				},
			},
		},
	},
};

// --- Math Exercise (MathLive input, lazy-loaded) ---
const mathExercise: InteractiveSpec = {
	root: 'math-card',
	elements: {
		'math-card': {
			type: 'Card',
			props: {
				title: 'Your answer',
				description: 'Use the math keyboard to enter your answer',
			},
			children: ['math-input'],
		},
		'math-input': {
			type: 'MathExercise',
			props: {
				placeholder: 'Tap to start typing your answer...',
				submitLabel: 'Check my answer',
				prefill: null,
			},
		},
	},
};

// --- Chart (data-driven: representative buildDataDrivenSpec output) ---
const chart: InteractiveSpec = {
	root: 'chart-root',
	elements: {
		'chart-root': {
			type: 'Chart',
			props: {
				type: 'bar',
				title: 'Minutes studied this week',
				data: [
					{ label: 'Mon', value: 25 },
					{ label: 'Tue', value: 40 },
					{ label: 'Wed', value: 15 },
					{ label: 'Thu', value: 45 },
					{ label: 'Fri', value: 30 },
				],
				xLabel: 'Day',
				yLabel: 'Minutes',
			},
		},
	},
};

// --- Step Explainer (data-driven: representative buildDataDrivenSpec output) ---
const stepExplainer: InteractiveSpec = {
	root: 'step-root',
	elements: {
		'step-root': {
			type: 'StepExplainer',
			props: {
				title: 'Solving 2x + 6 = 14',
				steps: [
					{
						label: 'Isolate the variable term',
						content: 'Subtract 6 from both sides: 2x = 8',
					},
					{
						label: 'Solve for x',
						content: 'Divide both sides by 2: x = 4',
					},
					{
						label: 'Check your answer',
						content: 'Substitute back: 2(4) + 6 = 14 ✓',
					},
				],
			},
		},
	},
};

const allSpecs = [
	{ name: '1. Homework Help', marker: 'homework-help', spec: homeworkHelp },
	{ name: '2. Help Style', marker: 'homework-mode', spec: homeworkMode },
	{ name: '3. Quiz Prompt', marker: 'homework-quiz', spec: homeworkQuiz },
	{ name: '4. Study Tools', marker: 'study-tools', spec: studyTools },
	{
		name: '5. Progress Encouragement',
		marker: 'homework-progress',
		spec: homeworkProgress,
	},
	{ name: '6. Explain More', marker: 'explain-more', spec: explainMore },
	{ name: '7. Math Exercise', marker: 'math-exercise', spec: mathExercise },
	{ name: '8. Chart (data-driven)', marker: 'chart', spec: chart },
	{
		name: '9. Step Explainer (data-driven)',
		marker: 'step-explainer',
		spec: stepExplainer,
	},
];

function InteractiveDemoPage() {
	const [lastAction, setLastAction] = useState<string | null>(null);

	useEffect(() => {
		const handler = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			if (detail?.type === 'auto_send') {
				setLastAction(`auto_send: "${detail.text}"`);
			} else if (detail?.type === 'send_message') {
				setLastAction(`send_message: "${detail.text}"`);
			} else if (detail?.type === 'open_link') {
				setLastAction(`open_link: ${detail.url}`);
			}
		};
		window.addEventListener('interactive-action', handler);
		return () => window.removeEventListener('interactive-action', handler);
	}, []);

	return (
		<div className="flex-1 overflow-y-auto bg-background p-8">
			<div className="max-w-2xl mx-auto space-y-8">
				<div>
					<h1 className="text-2xl font-bold text-foreground">Interactive Component Gallery</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Local test harness for the json-render interactive-message mechanism. Mirrors the
						production specs from cdk/lambda/agent-handler/interactive-specs.ts; click any control
						and the banner shows the action it dispatches.
					</p>
				</div>

				{lastAction && (
					<div className="sticky top-4 z-10 bg-secondary border border-border rounded-lg p-3 text-sm text-foreground shadow-md">
						Last action: <code className="font-mono">{lastAction}</code>
					</div>
				)}

				<div className="space-y-6">
					<Message align="end">
						<MessageContent>
							<Bubble className="max-w-[85%]">
								<BubbleContent>Hi, I could use some help today</BubbleContent>
							</Bubble>
						</MessageContent>
					</Message>

					{allSpecs.map(({ name, marker, spec }) => (
						<div key={name} className="space-y-1.5">
							<div className="flex items-center gap-2 pl-1">
								<p className="text-xs font-semibold text-foreground">{name}</p>
								<code className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
									[INTERACTIVE:{marker}]
								</code>
							</div>
							<Message>
								<MessageContent>
									<Bubble variant="secondary" className="w-full max-w-sm">
										<BubbleContent className="w-full">
											<Suspense fallback={<span className="text-xs italic">Loading…</span>}>
												<InteractiveMessage spec={spec} />
											</Suspense>
										</BubbleContent>
									</Bubble>
								</MessageContent>
							</Message>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
